import { type ReactNode, useCallback } from 'react';
import styled from 'styled-components';
import { Spacing8 } from 'autogen/design-tokens/tokens';
import { Typography } from 'components/generic/typography';
import Icon from 'components/Icon';
import FlexRow from 'components/Layout/components/Flex/components/FlexRow';
import { createListItemVariants } from 'components/List/shared/helpers';
import type { ListItemType } from 'components/List/shared/types';
import Checkbox from 'components/shared/inputs/checkbox';
import Radio from 'components/shared/inputs/radio';
import ListItem from '../ListItem/ListItem';

const Text = styled(Typography.Body)`
    line-height: 1;
`;

const BoldText = styled(Text)`
    font-weight: bold;
`;

const SelectedPreviewText = styled(BoldText)`
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const CountText = styled(Text)`
    color: ${({ theme }) => theme.ColorTextSecondary};
`;

const StyledIcon = styled(Icon)`
    flex: none;
`;

type NavigationTypeProps = ListItemType & {
    type: 'navigation';
    selectedPreview?: ReactNode;
    count?: never;
    isSelected?: never;
};

type SingleSelectTypeProps = ListItemType & {
    type: 'single select';
    count?: number;
    isSelected?: boolean;
    selectedPreview?: never;
};

type MultiSelectTypeProps = ListItemType & {
    type: 'multi select';
    count?: number;
    isSelected?: boolean;
    selectedPreview?: never;
};

export type FilterListItemProps = NavigationTypeProps | SingleSelectTypeProps | MultiSelectTypeProps;

function FilterListItem(props: FilterListItemProps) {
    const { type, children, count, isSelected, selectedPreview, ...listItemProps } = props;

    const AfterRenderer = useCallback(() => {
        switch (type) {
            case 'multi select':
                return (
                    <FlexRow gap={Spacing8} alignItems="center">
                        {count && <CountText size="M">({count})</CountText>}
                        <Checkbox readOnly checked={isSelected} tabIndex={-1} />
                    </FlexRow>
                );
            case 'single select':
                return (
                    <FlexRow gap={Spacing8} alignItems="center">
                        {count && <CountText size="M">({count})</CountText>}
                        <Radio readOnly checked={isSelected} />
                    </FlexRow>
                );
            case 'navigation':
                return (
                    <FlexRow gap={Spacing8} alignItems="center">
                        <SelectedPreviewText size="M">{selectedPreview}</SelectedPreviewText>
                        <StyledIcon type="ChevronRight" size={16} />
                    </FlexRow>
                );
            default:
                return <StyledIcon type="ChevronRight" size={16} />;
        }
    }, [type, count, isSelected, selectedPreview]);

    return (
        <ListItem AfterRenderer={AfterRenderer} {...listItemProps}>
            {children}
        </ListItem>
    );
}

const listItemVariants = createListItemVariants(FilterListItem);

export default Object.assign(FilterListItem, listItemVariants);
